import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Package, Printer, Layers, FlaskConical, CircleDot,
  LogOut, Users, Menu, X, ClipboardList, Calendar, Activity, CheckSquare,
  AlertTriangle, Wrench, DollarSign, Leaf, Megaphone, BookOpen, Newspaper,
  Lightbulb, CalendarDays, ShoppingBag, FileEdit, Plus, Wand2,
  Link, ExternalLink, GripVertical, Check, KeyRound, LineChart, Plane, ChevronDown,
  Sun, Moon, Camera, NotebookPen, MessageSquare, KanbanSquare, TrendingUp, Mic,
  Mail, HardDrive, Presentation, Building2,
} from 'lucide-react';
import api from '../utils/api';
import dayjs from 'dayjs';
import { ChatProvider } from '../contexts/ChatContext';
import { useChat } from '../contexts/useChat';
import GoogleChatPanel from './chat/GoogleChatPanel';
import MeetingPanel from './chat/MeetingPanel';

// 상단 네비게이션의 '채팅' 메뉴 노출 여부.
// 라우트(/chat)와 패널 자체는 그대로 두고 진입 메뉴만 감춘다 —
// 다시 보이게 하려면 이 값만 true 로 바꾸면 된다.
const SHOW_CHAT_MENU = false;
import { useTheme } from '../contexts/ThemeContext';



const TABS = [
  { key: 'work',  label: '그룹웨어', short: '그룹웨어' },
  { key: 'event', label: '업무관리', short: '업무관리' },
];

const SUB_MENUS = {
  work: [
    { to: '/notices',         icon: Megaphone,   label: '공지사항' },
    { to: '/calendar',        icon: Calendar,    label: '캘린더' },
    { to: '/tasks',           icon: CheckSquare, label: '할일' },
    { to: '/work/meeting',    icon: NotebookPen, label: '회의록' },
    { to: '/research',        icon: BookOpen,    label: '기록원' },
  ],
  event: [
    { to: '/events',     icon: CalendarDays, label: '행사관리' },
    { to: '/recruit',    icon: Megaphone,    label: '섭외리스트' },
    { to: '/partners',   icon: Users,        label: '파트너사' },
    { to: '/quotations',         icon: FileEdit,  label: '견적서' },
    { to: '/competitor-quotes', icon: Building2, label: '타견적서' },
    { to: '/proposals',  icon: Presentation, label: '기획안' },
  ],
};

function getFavicon(url) {
  try { return `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`; }
  catch { return null; }
}

function QuickLinksDropdown() {
  const [links, setLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', url: '' });
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', url: '' });
  const user = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
  const isSuperadmin = user.role === 'superadmin';
  const { theme } = useTheme();
  const btnRef = useRef(null);
  const dropRef = useRef(null);

  const load = () => api.get('/quick-links').then(r => setLinks(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        dropRef.current && !dropRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) { setOpen(false); setShowForm(false); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const addLink = async () => {
    if (!form.title.trim() || !form.url.trim()) return;
    let url = form.url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;
    await api.post('/quick-links', { title: form.title.trim(), url });
    setForm({ title: '', url: '' });
    setShowForm(false);
    load();
  };

  const del = async (id) => {
    await api.delete(`/quick-links/${id}`);
    load();
  };

  const startEdit = (lk) => {
    setEditingId(lk.id);
    setEditForm({ title: lk.title, url: lk.url });
  };

  const saveEdit = async () => {
    if (!editForm.title.trim() || !editForm.url.trim()) return;
    let url = editForm.url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;
    await api.put(`/quick-links/${editingId}`, { title: editForm.title.trim(), url });
    setEditingId(null);
    load();
  };

  const onDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const onDrop = async (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const reordered = [...links];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setLinks(reordered);
    setDragIdx(null); setDragOverIdx(null);
    await Promise.all(reordered.map((lk, i) => api.put(`/quick-links/${lk.id}`, { sortOrder: i })));
  };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  if (!isSuperadmin && links.length === 0) return null;

  return (
    <div className="relative flex-shrink-0">
      <button
        ref={btnRef}
        onClick={() => { setOpen(o => !o); setShowForm(false); }}
        title="빠른 링크"
        className={`p-1.5 rounded-lg transition-all duration-200 flex items-center gap-1 shimmer-hover ${open ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white hover:bg-white/6'}`}
      >
        <Link size={14} />
        {links.length > 0 && (
          <span className="text-[9px] text-gray-500 tabular-nums">{links.length}</span>
        )}
      </button>

      {open && (
        <div
          ref={dropRef}
          className={`absolute right-0 top-full mt-1 w-56 rounded-xl z-50 overflow-hidden animate-fade-in glass-chrome border ${theme === 'dark' ? 'border-white/8' : 'border-gray-200'}`}
          style={{
            background: theme === 'dark' ? 'rgba(10,12,20,0.92)' : '#ffffff',
            boxShadow: theme === 'dark' ? '0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)' : '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          {/* 링크 목록 */}
          {links.length > 0 && (
            <div className="py-1 max-h-72 overflow-y-auto">
              {links.map((lk, idx) => (
                <div
                  key={lk.id}
                  className={`relative group ${dragOverIdx === idx && dragIdx !== idx ? 'bg-white/6' : ''}`}
                  draggable={isSuperadmin && editingId !== lk.id}
                  onDragStart={isSuperadmin && editingId !== lk.id ? e => onDragStart(e, idx) : undefined}
                  onDragOver={isSuperadmin ? e => onDragOver(e, idx) : undefined}
                  onDrop={isSuperadmin ? e => onDrop(e, idx) : undefined}
                  onDragEnd={isSuperadmin ? onDragEnd : undefined}
                  style={{ opacity: dragIdx === idx ? 0.4 : 1 }}
                >
                  {editingId === lk.id ? (
                    <div className="px-3 py-2 space-y-1.5">
                      <input
                        value={editForm.title}
                        onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                        placeholder="링크 이름"
                        autoFocus
                        className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-brand placeholder-gray-600"
                      />
                      <input
                        value={editForm.url}
                        onChange={e => setEditForm(f => ({ ...f, url: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && saveEdit()}
                        placeholder="https://..."
                        className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-brand placeholder-gray-600"
                      />
                      <div className="flex gap-1.5">
                        <button onClick={saveEdit} className="flex-1 py-1 bg-brand text-white text-xs rounded hover:bg-brand/80 flex items-center justify-center gap-1">
                          <Check size={10} /> 저장
                        </button>
                        <button onClick={() => setEditingId(null)} className="px-2 py-1 text-gray-500 hover:text-white text-xs">취소</button>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={lk.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => { dragIdx !== null && e.preventDefault(); setOpen(false); }}
                      className={`flex items-center gap-2 px-3 py-2 transition-colors ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                      title={lk.url}
                    >
                      {isSuperadmin && <GripVertical size={10} className="text-gray-700 cursor-grab flex-shrink-0" />}
                      <img src={getFavicon(lk.url)} alt="" className="w-3.5 h-3.5 rounded-sm flex-shrink-0"
                        onError={e => { e.target.style.display = 'none'; }} />
                      <span className="text-gray-300 text-xs flex-1 truncate">{lk.title}</span>
                      <ExternalLink size={9} className="text-gray-600 flex-shrink-0" />
                    </a>
                  )}
                  {isSuperadmin && editingId !== lk.id && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={e => { e.preventDefault(); startEdit(lk); }}
                        className="w-4 h-4 bg-gray-700 hover:bg-brand rounded-full flex items-center justify-center"
                      >
                        <FileEdit size={8} className="text-white" />
                      </button>
                      <button
                        onClick={() => del(lk.id)}
                        className="w-4 h-4 bg-gray-700 hover:bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <X size={8} className="text-white" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 슈퍼관리자: 링크 추가 */}
          {isSuperadmin && (
            <div className={`border-t ${theme === 'dark' ? 'border-white/6' : 'border-gray-100'} px-3 py-2 ${links.length === 0 ? 'border-t-0' : ''}`}>
              {showForm ? (
                <div className="space-y-1.5">
                  <input
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="링크 이름"
                    autoFocus
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-brand placeholder-gray-600"
                  />
                  <input
                    value={form.url}
                    onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && addLink()}
                    placeholder="https://..."
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-brand placeholder-gray-600"
                  />
                  <div className="flex gap-1.5">
                    <button onClick={addLink} className="flex-1 py-1 bg-brand text-white text-xs rounded hover:bg-brand/80 flex items-center justify-center gap-1">
                      <Check size={10} /> 추가
                    </button>
                    <button onClick={() => { setShowForm(false); setForm({ title: '', url: '' }); }}
                      className="px-2 py-1 text-gray-500 hover:text-white text-xs">취소</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowForm(true)}
                  className="w-full flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-xs py-0.5 transition-colors"
                >
                  <Plus size={11} /> 링크 추가
                </button>
              )}
            </div>
          )}

          {links.length === 0 && !isSuperadmin && (
            <div className="px-3 py-4 text-center text-gray-600 text-xs">링크 없음</div>
          )}
        </div>
      )}
    </div>
  );
}

function detectTab(pathname) {
  if (pathname.startsWith('/events') || pathname.startsWith('/partners') || pathname.startsWith('/recruit')) return 'event';
  return 'work';
}

function LayoutInner({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  const { meetingChannel, meetingOpen, setMeetingOpen } = useChat();
  const user = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
  const isSuperadmin = user.role === 'superadmin';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => detectTab(location.pathname));
  const [showUserMenu, setShowUserMenu] = useState(false);
  const chatMode = location.pathname.startsWith('/chat');
  const fullscreenMode = chatMode || location.pathname === '/gmail' || location.pathname === '/drive';
  const [newBadges, setNewBadges] = useState(new Set());
  const [notifCounts, setNotifCounts] = useState({ gmailUnread: 0, driveNew: 0 });
  const [gchatUnread, setGchatUnread] = useState(() => parseInt(localStorage.getItem('gchat_unread') || '0', 10));
  const scrollRef = useRef(null);

  useEffect(() => {
    const handler = (e) => setGchatUnread(e.detail.count);
    window.addEventListener('gchat-unread', handler);
    return () => window.removeEventListener('gchat-unread', handler);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    api.post('/access-logs', { path: location.pathname }).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    api.get('/layout/new-badges')
      .then(r => setNewBadges(new Set(r.data.paths)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchCounts = () => {
      api.get('/layout/notification-counts')
        .then(r => setNotifCounts(r.data))
        .catch(() => {});
    };
    fetchCounts();
    const timer = setInterval(fetchCounts, 60000);
    return () => clearInterval(timer);
  }, []);

  const [showPwModal, setShowPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl || null);
  const avatarInputRef = useRef(null);
  useEffect(() => {
    api.get('/admins/me').then(res => {
      const url = res.data.avatarUrl || null;
      setAvatarUrl(url);
      const stored = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
      if (stored.avatarUrl !== url) localStorage.setItem('paper_work_user', JSON.stringify({ ...stored, avatarUrl: url }));
    }).catch(() => {});
  }, []);
  const uploadAvatar = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const fd = new FormData(); fd.append('avatar', file);
    try {
      const res = await api.post('/admins/me/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.avatarUrl;
      setAvatarUrl(url);
      const stored = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
      localStorage.setItem('paper_work_user', JSON.stringify({ ...stored, avatarUrl: url }));
    } catch {}
  };

  const submitPwChange = async () => {
    setPwError('');
    if (!pwForm.current || !pwForm.next) { setPwError('모든 항목을 입력하세요.'); return; }
    if (pwForm.next !== pwForm.confirm) { setPwError('새 비밀번호가 일치하지 않습니다.'); return; }
    if (pwForm.next.length < 4) { setPwError('새 비밀번호는 4자 이상이어야 합니다.'); return; }
    try {
      await api.put('/auth/password', { currentPassword: pwForm.current, newPassword: pwForm.next });
      setPwSuccess(true);
      setTimeout(() => { setShowPwModal(false); setPwForm({ current: '', next: '', confirm: '' }); setPwSuccess(false); }, 1200);
    } catch (e) {
      setPwError(e.response?.data?.message || '변경 실패');
    }
  };

  const subMenus = SUB_MENUS[activeTab] || [];
  const [superadminOpen, setSuperadminOpen] = useState(false);

  const logout = () => {
    localStorage.removeItem('paper_work_token');
    localStorage.removeItem('paper_work_user');
    navigate('/login');
  };

  const handleTabClick = (key) => {
    setActiveTab(key);
    const first = SUB_MENUS[key].find(m => m.to);
    if (first) navigate(first.to);
    setDrawerOpen(false);
  };

  const sidebarContent = fullscreenMode ? null : (
    <div className="flex flex-col h-full">
      <nav className="flex-1 overflow-y-auto py-3">
        {subMenus.map((item, i) => {
          if (item.type === 'section') {
            return (
              <div key={i} className="mt-4 mb-1">
                <div className="px-4 py-1.5"
                  style={theme === 'dark' ? { background: 'rgba(255,255,255,0.03)', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' } : {}}>
                  <span className="text-[10px] text-gray-400 font-semibold tracking-widest uppercase">
                    {item.label}
                  </span>
                </div>
              </div>
            );
          }
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setDrawerOpen(false)}
              className={({ isActive }) =>
                `nav-link flex items-center gap-2.5 px-4 py-2 text-sm mb-0.5 ${
                  isActive
                    ? 'nav-active text-brand font-medium'
                    : theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`
              }
              style={({ isActive }) => isActive ? { background: 'linear-gradient(90deg, rgba(224,0,94,0.14) 0%, rgba(224,0,94,0.02) 100%)' } : {}}
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="nav-active-bar" />}
                  <Icon size={15} className={`nav-icon ${isActive ? 'text-brand' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className="nav-label">{item.label}</span>
                  {newBadges.has(item.to) && !isActive && (
                    <span className="ml-auto text-[9px] font-bold bg-brand text-white px-1.5 py-0.5 rounded-full leading-none">N</span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}

        {/* 슈퍼관리자 전용 — 업무 탭에서만 노출 */}
        {isSuperadmin && activeTab === 'work' && (
          <div className="mt-4">
            <button
              onClick={() => setSuperadminOpen(o => !o)}
              className={`w-full px-4 py-1.5 flex items-center justify-between transition-all duration-200 ${theme === 'dark' ? 'hover:bg-white/[0.04]' : 'hover:bg-gray-100'}`}
              style={theme === 'dark' ? { background: 'rgba(255,255,255,0.03)', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' } : {}}
            >
              <span className="text-[10px] text-gray-400 font-semibold tracking-widest uppercase">슈퍼관리자</span>
              <ChevronDown size={13} className={`text-gray-500 transition-transform ${superadminOpen ? 'rotate-180' : ''}`} />
            </button>
            {superadminOpen && (
              <>
                <NavLink
                  to="/work/plaud"
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    `nav-link flex items-center gap-2.5 px-4 py-2 text-sm mb-0.5 ${
                      isActive ? 'nav-active text-brand font-medium' : theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`
                  }
                  style={({ isActive }) => isActive ? { background: 'linear-gradient(90deg, rgba(224,0,94,0.14) 0%, rgba(224,0,94,0.02) 100%)' } : {}}
                >
                  {({ isActive }) => (<>
                    {isActive && <span className="nav-active-bar" />}
                    <Mic size={15} className={`nav-icon ${isActive ? 'text-brand' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className="nav-label">Plaud 녹음 관리</span>
                  </>)}
                </NavLink>
                <NavLink
                  to="/admins"
                  end
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    `nav-link flex items-center gap-2.5 px-4 py-2 text-sm mb-0.5 ${
                      isActive ? 'nav-active text-brand font-medium' : theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`
                  }
                  style={({ isActive }) => isActive ? { background: 'linear-gradient(90deg, rgba(224,0,94,0.14) 0%, rgba(224,0,94,0.02) 100%)' } : {}}
                >
                  {({ isActive }) => (<>
                    {isActive && <span className="nav-active-bar" />}
                    <Users size={15} className={`nav-icon ${isActive ? 'text-brand' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className="nav-label">직원 관리</span>
                  </>)}
                </NavLink>
                <NavLink
                  to="/admins/access-logs"
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    `nav-link flex items-center gap-2.5 px-4 py-2 text-sm mb-0.5 ${
                      isActive ? 'nav-active text-brand font-medium' : theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`
                  }
                  style={({ isActive }) => isActive ? { background: 'linear-gradient(90deg, rgba(224,0,94,0.14) 0%, rgba(224,0,94,0.02) 100%)' } : {}}
                >
                  {({ isActive }) => (<>
                    {isActive && <span className="nav-active-bar" />}
                    <LineChart size={15} className={`nav-icon ${isActive ? 'text-brand' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className="nav-label">접속 로그 분석</span>
                  </>)}
                </NavLink>
              </>
            )}
          </div>
        )}
      </nav>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-950">
      {/* Ambient background orbs */}
      <div className="ambient-bg">
        <div className="orb-1" />
        <div className="orb-2" />
        <div className="orb-3" />
        <div className="orb-4" />
      </div>

      {/* 유저 메뉴 backdrop */}
      {showUserMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
      )}
      {/* 상단 바 */}
      <header className="fixed top-0 left-0 right-0 z-50 h-12 flex items-center glass-chrome"
        style={{
          background: theme === 'dark' ? 'rgba(6,8,15,0.82)' : '#ffffff',
          borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,0.07)' : '1px solid #e5e7eb',
        }}>
        {/* 모바일 햄버거 (로고 왼쪽) */}
        <button onClick={() => setDrawerOpen(o => !o)} className="md:hidden p-1.5 text-gray-400 hover:text-white flex-shrink-0 ml-1">
          {drawerOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        {/* 로고 영역 */}
        <div className="flex-shrink-0 flex items-center gap-2 px-2 md:w-52 md:px-4">
          <img src="/pinkpaper_logo.png" alt="핑크페이퍼" className="h-6 w-auto" style={{ filter: theme === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(0)' }} />
          <span className={`text-xs font-medium hidden md:block whitespace-nowrap ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>그룹웨어</span>
        </div>

        {/* 탭 */}
        <nav className="flex items-center gap-1 flex-1 overflow-x-auto scrollbar-none px-1 md:px-3">
          {TABS.map(tab => {
            const isGooglePage = location.pathname.startsWith('/gmail') || location.pathname.startsWith('/drive') || location.pathname.startsWith('/google');
            const isActive = !chatMode && !isGooglePage && activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => { handleTabClick(tab.key); }}
                className={`flex-shrink-0 px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-semibold tracking-wide transition-all duration-200 whitespace-nowrap shimmer-hover ${
                  isActive
                    ? 'text-white'
                    : 'text-gray-500 hover:text-gray-200 hover:bg-white/[0.06]'
                }`}
                style={isActive ? {
                  background: 'linear-gradient(135deg, rgba(224,0,94,0.22), rgba(224,0,94,0.08))',
                  border: '1px solid rgba(224,0,94,0.3)',
                } : { border: '1px solid transparent' }}
              >
                <span className="md:hidden">{tab.short}</span>
                <span className="hidden md:inline">{tab.label}</span>
              </button>
            );
          })}
          <div className="w-px h-4 mx-1.5 flex-shrink-0" style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db' }} />
          {SHOW_CHAT_MENU && (
            <button
              onClick={() => { if (!chatMode) navigate('/chat'); }}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-semibold tracking-wide transition-all duration-200 whitespace-nowrap shimmer-hover ${
                chatMode ? 'text-white' : 'text-gray-500 hover:text-gray-200 hover:bg-white/[0.06]'
              }`}
              style={chatMode ? {
                background: 'linear-gradient(135deg, rgba(224,0,94,0.22), rgba(224,0,94,0.08))',
                border: '1px solid rgba(224,0,94,0.3)',
              } : { border: '1px solid transparent' }}
            >
              <MessageSquare size={13} className="flex-shrink-0" />
              <span>채팅</span>
              {gchatUnread > 0 && (
                <span className="ml-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[1rem] h-4 flex items-center justify-center px-1 leading-none">
                  {gchatUnread > 99 ? '99+' : gchatUnread}
                </span>
              )}
            </button>
          )}
          <NavLink
            to="/gmail"
            onClick={() => {}}
            className={({ isActive }) => `flex-shrink-0 flex items-center gap-1.5 px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-semibold tracking-wide transition-all duration-200 whitespace-nowrap shimmer-hover ${
              isActive && !chatMode ? 'text-white' : 'text-gray-500 hover:text-gray-200 hover:bg-white/[0.06]'
            }`}
            style={({ isActive }) => isActive && !chatMode ? {
              background: 'linear-gradient(135deg, rgba(224,0,94,0.22), rgba(224,0,94,0.08))',
              border: '1px solid rgba(224,0,94,0.3)',
            } : { border: '1px solid transparent' }}
          >
            <Mail size={13} className="flex-shrink-0" />
            <span>메일</span>
            {notifCounts.gmailUnread > 0 && (
              <span className="ml-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[1rem] h-4 flex items-center justify-center px-1 leading-none">
                {notifCounts.gmailUnread > 99 ? '99+' : notifCounts.gmailUnread}
              </span>
            )}
          </NavLink>
          <NavLink
            to="/drive"
            onClick={() => {}}
            className={({ isActive }) => `flex-shrink-0 flex items-center gap-1.5 px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-semibold tracking-wide transition-all duration-200 whitespace-nowrap shimmer-hover ${
              isActive && !chatMode ? 'text-white' : 'text-gray-500 hover:text-gray-200 hover:bg-white/[0.06]'
            }`}
            style={({ isActive }) => isActive && !chatMode ? {
              background: 'linear-gradient(135deg, rgba(224,0,94,0.22), rgba(224,0,94,0.08))',
              border: '1px solid rgba(224,0,94,0.3)',
            } : { border: '1px solid transparent' }}
          >
            <HardDrive size={13} className="flex-shrink-0" />
            <span>드라이브</span>
            {notifCounts.driveNew > 0 && (
              <span className="ml-0.5 bg-brand text-white text-[9px] font-bold rounded-full min-w-[1rem] h-4 flex items-center justify-center px-1 leading-none">
                N
              </span>
            )}
          </NavLink>
        </nav>

        {/* 사용자 메뉴 (데스크톱) */}
        <div className="hidden md:flex items-center gap-1 px-3 flex-shrink-0">
          <QuickLinksDropdown />
          <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(v => !v)}
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-white/6 transition-all duration-200 shimmer-hover"
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt={user.name} className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-brand/20 text-brand text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                  {(user.name || user.username || '?')[0].toUpperCase()}
                </div>
              )}
              <span className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{user.name || user.username}</span>
              <ChevronDown size={11} className={`text-gray-500 transition-transform duration-150 ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {showUserMenu && (
              <div className={`absolute right-0 top-full mt-1 w-52 rounded-xl z-[60] overflow-hidden animate-fade-in glass-chrome ${theme === 'light' ? 'border border-gray-200' : ''}`}
                style={{ background: theme === 'light' ? '#ffffff' : 'rgba(15,17,26,0.95)', boxShadow: theme === 'light' ? '0 4px 20px rgba(0,0,0,0.08)' : '0 8px 32px rgba(0,0,0,0.6)' }}>
                {/* 유저 정보 헤더 */}
                <div className={`px-4 py-3 border-b ${theme === 'dark' ? 'border-white/8' : 'border-gray-100'}`}>
                  <div className="flex items-center gap-2.5">
                    <div className="relative group/avatar cursor-pointer" onClick={() => avatarInputRef.current?.click()}>
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-brand/20 text-brand text-sm font-bold flex items-center justify-center">
                          {(user.name || user.username || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover/avatar:opacity-100 flex items-center justify-center transition-opacity">
                        <Camera size={12} className="text-white" />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{user.name || user.username}</p>
                      <p className={`text-[11px] ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>{user.role === 'superadmin' ? '최고관리자' : '관리자'}</p>
                    </div>
                  </div>
                </div>

                {/* 테마 토글 */}
                <div className={`px-4 py-2.5 border-b ${theme === 'dark' ? 'border-white/8' : 'border-gray-100'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{theme === 'dark' ? '다크 모드' : '라이트 모드'}</span>
                    <div className="flex items-center gap-1.5">
                      <Moon size={10} className="text-gray-500" />
                      <button
                        onClick={toggleTheme}
                        className="relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none"
                        style={{ backgroundColor: theme === 'light' ? '#f4b8b8' : '#4b5563' }}
                      >
                        <span
                          className="absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200"
                          style={{ left: '2px', transform: theme === 'light' ? 'translateX(16px)' : 'translateX(0)' }}
                        />
                      </button>
                      <Sun size={10} className="text-gray-500" />
                    </div>
                  </div>
                </div>

                {/* 비밀번호 변경 */}
                <button
                  onClick={() => { setShowUserMenu(false); setShowPwModal(true); setPwError(''); setPwSuccess(false); }}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 transition-colors text-sm border-b ${theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-white/5 border-white/8' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-gray-100'}`}
                >
                  <KeyRound size={14} />
                  비밀번호 변경
                </button>

                {/* 로그아웃 */}
                <button
                  onClick={() => { setShowUserMenu(false); logout(); }}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 transition-colors text-sm ${theme === 'dark' ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20' : 'text-red-500 hover:text-red-600 hover:bg-red-50'}`}
                >
                  <LogOut size={14} />
                  로그아웃
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 모바일: 퀵링크 + 아바타 드롭다운 */}
        <div className="md:hidden flex items-center gap-1 pr-2 flex-shrink-0">
          <QuickLinksDropdown />
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(v => !v)}
              className="w-7 h-7 rounded-full overflow-hidden bg-brand/20 text-brand text-xs font-bold flex items-center justify-center"
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt={user.name} className="w-full h-full object-cover" />
              ) : (user.name || user.username || '?')[0].toUpperCase()}
            </button>
            {showUserMenu && (
              <div className={`absolute right-0 top-full mt-1 w-48 rounded-xl z-[60] overflow-hidden animate-fade-in glass-chrome ${theme === 'light' ? 'border border-gray-200' : ''}`}
                style={{ background: theme === 'dark' ? 'rgba(10,12,20,0.92)' : '#ffffff', boxShadow: theme === 'dark' ? '0 24px 64px rgba(0,0,0,0.7)' : '0 4px 20px rgba(0,0,0,0.08)' }}>
                <div className={`px-4 py-2.5 border-b ${theme === 'dark' ? 'border-white/8' : 'border-gray-100'}`}>
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{user.name || user.username}</p>
                </div>
                <div className={`px-4 py-2.5 border-b ${theme === 'dark' ? 'border-white/8' : 'border-gray-100'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{theme === 'dark' ? '다크' : '라이트'}</span>
                    <div className="flex items-center gap-1.5">
                      <Moon size={10} className="text-gray-500" />
                      <button onClick={toggleTheme}
                        className="relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none"
                        style={{ backgroundColor: theme === 'light' ? '#f4b8b8' : '#4b5563' }}>
                        <span className="absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200"
                          style={{ left: '2px', transform: theme === 'light' ? 'translateX(16px)' : 'translateX(0)' }} />
                      </button>
                      <Sun size={10} className="text-gray-500" />
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => { setShowUserMenu(false); setShowPwModal(true); setPwError(''); setPwSuccess(false); }}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm border-b ${theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-white/5 border-white/8' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-gray-100'}`}>
                  <KeyRound size={13} /> 비밀번호 변경
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); logout(); }}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm ${theme === 'dark' ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20' : 'text-red-500 hover:text-red-600 hover:bg-red-50'}`}>
                  <LogOut size={13} /> 로그아웃
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* 데스크톱 좌측 사이드바 */}
      <aside className={`${fullscreenMode ? 'hidden' : 'hidden md:flex'} w-52 flex-col flex-shrink-0 fixed top-12 bottom-0 left-0 glass-chrome z-20`}
        style={{
          background: 'var(--sidebar-bg)',
          borderRight: theme === 'dark' ? '1px solid rgba(255,255,255,0.07)' : '1px solid #e5e7eb',
        }}>
        {sidebarContent}
      </aside>

      {/* 모바일 드로어 */}
      {drawerOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-40 backdrop-blur-sm"
            style={{ background: theme === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(0,0,0,0.3)' }}
            onClick={() => setDrawerOpen(false)} />
          <aside className="md:hidden fixed top-12 left-0 bottom-0 w-52 z-50 flex flex-col animate-slide-in-left glass-chrome"
            style={{
              background: 'var(--sidebar-bg)',
              borderRight: theme === 'dark' ? '1px solid rgba(255,255,255,0.07)' : '1px solid #e5e7eb',
            }}>
            {sidebarContent}
          </aside>
        </>
      )}

      {/* 메인 콘텐츠 */}
      <main className={`flex-1 ${fullscreenMode ? '' : 'md:ml-52'} flex flex-col overflow-hidden relative z-10`} style={{ marginTop: '3rem', height: 'calc(100dvh - 3rem)' }}>
        {chatMode ? (
          <div className="flex-1 min-h-0">
            <GoogleChatPanel mode="main" />
          </div>
        ) : (location.pathname === '/gmail' || location.pathname === '/drive') ? (
          <div key={location.pathname} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
            {children}
          </div>
        ) : (
          <div ref={scrollRef} className="flex-1 overflow-auto p-4 sm:p-6 flex flex-col">
            {/* min-h-0 필수: 이게 없으면 overflow가 visible 인 플렉스 아이템의
                자동 최소 높이가 콘텐츠 크기로 잡혀, 목록이 길어질 때 이 div 가
                같이 늘어난다. 그러면 페이지 루트의 h-full 이 늘어난 높이를 받아
                목록의 overflow-y-auto 가 작동하지 못하고 페이지 전체가 길어졌다. */}
            <div key={location.pathname} className="page-enter flex-1 min-h-0 flex flex-col">
              {children}
            </div>
          </div>
        )}
      </main>

      <MeetingPanel open={meetingOpen} onClose={() => setMeetingOpen(false)} channel={meetingChannel} />

      {/* 비밀번호 변경 모달 */}
      {showPwModal && (
        <div className="fixed inset-0 backdrop-blur-md z-50 flex items-center justify-center p-4"
          style={{ background: theme === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(0,0,0,0.25)' }}>
          <div className="rounded-xl w-full max-w-sm p-6 glass-chrome animate-fade-in"
            style={{
              background: theme === 'dark' ? 'rgba(10,12,22,0.92)' : 'rgba(255,255,255,0.95)',
              border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              boxShadow: '0 40px 80px rgba(0,0,0,0.5)',
            }}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <KeyRound size={16} className="text-brand" />
                <span className="text-white font-semibold text-sm">비밀번호 변경</span>
              </div>
              <button onClick={() => setShowPwModal(false)} className="text-gray-500 hover:text-white">
                <X size={16} />
              </button>
            </div>
            {pwSuccess ? (
              <p className="text-green-400 text-sm text-center py-4">변경됐습니다 ✓</p>
            ) : (
              <div className="space-y-3">
                <input
                  type="password"
                  placeholder="현재 비밀번호"
                  value={pwForm.current}
                  onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && submitPwChange()}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600"
                />
                <input
                  type="password"
                  placeholder="새 비밀번호"
                  value={pwForm.next}
                  onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && submitPwChange()}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600"
                />
                <input
                  type="password"
                  placeholder="새 비밀번호 확인"
                  value={pwForm.confirm}
                  onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && submitPwChange()}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600"
                />
                {pwError && <p className="text-red-400 text-xs">{pwError}</p>}
                <button
                  onClick={submitPwChange}
                  className="w-full bg-brand hover:bg-brand/80 text-white text-sm rounded-lg py-2 font-medium transition-all duration-200 mt-1 shimmer-hover"
                >
                  변경
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  return (
    <ChatProvider>
      <LayoutInner>{children}</LayoutInner>
    </ChatProvider>
  );
}
